import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DrawerTabs, SearchableSelect, SideDrawerShell } from "../../common/components";
import { userExclusionsApi } from "../api/userExclusionsApi";
import { emailAuthApi } from "../../auth/api/emailAuthApi";
import "../../../V2SideDrawer.css";
import "../styles/UserCreateDrawer.css";
import "../styles/UserEditDrawer.css";

const ROLE_OPTIONS = ["", "Super Admin", "Admin", "AM", "Project Lead", "Developer", "Guest", "External"];
const USER_TYPE_OPTIONS = ["", "Internal", "External", "Guest", "Admin"];

function MultiSelectDropdown({
  label,
  options,
  selectedIds,
  onChange,
  allLabel,
  placeholder,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [open]);

  const allIds = useMemo(() => (options || []).map((o) => o.id), [options]);
  const selectedSet = useMemo(() => new Set(selectedIds || []), [selectedIds]);
  const selectedOptions = useMemo(
    () => (options || []).filter((opt) => selectedSet.has(opt.id)),
    [options, selectedSet]
  );
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedSet.has(id));

  const toggleOne = (id) => {
    const next = selectedSet.has(id)
      ? (selectedIds || []).filter((x) => x !== id)
      : [...(selectedIds || []), id];
    onChange(next);
  };

  const toggleAll = () => {
    onChange(allSelected ? [] : allIds);
  };

  return (
    <div className="userc-field" ref={ref}>
      <label className="v2drw-form-label">{label}</label>
      <button type="button" className="userc-multi-trigger v2drw-control" onClick={() => setOpen((v) => !v)}>
        {selectedOptions.length === 0 ? (
          <span className="userc-multi-placeholder">{placeholder}</span>
        ) : (
          <span className="userc-multi-summary">{selectedOptions.length} selected</span>
        )}
        <span className="userc-multi-caret">▾</span>
      </button>
      {selectedOptions.length > 0 && (
        <div className="userc-multi-selected">
          {selectedOptions.map((opt) => (
            <span key={opt.id} className="userc-multi-pill">
              <span className="userc-multi-pill-label">{opt.label}</span>
              <span
                className="userc-multi-pill-remove"
                role="button"
                tabIndex={0}
                aria-label={`Remove ${opt.label}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleOne(opt.id);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleOne(opt.id);
                  }
                }}
              >
                ×
              </span>
            </span>
          ))}
        </div>
      )}
      {open && (
        <div className="userc-multi-drop">
          <label className="userc-multi-opt userc-multi-opt--all">
            <input type="checkbox" checked={allSelected} onChange={toggleAll} />
            {allLabel}
          </label>
          {(options || []).map((opt) => (
            <label key={opt.id} className="userc-multi-opt">
              <input
                type="checkbox"
                checked={selectedSet.has(opt.id)}
                onChange={() => toggleOne(opt.id)}
              />
              {opt.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

const SCOPE_LABELS = {
  timesheet_compliance: "Timesheet Compliance",
  avg_hrs: "Avg Hrs",
};
const SCOPE_OPTIONS = Object.entries(SCOPE_LABELS).map(([value, label]) => ({ value, label }));

function fmtDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = String(iso).split("T")[0].split("-");
  return `${d}/${m}/${y}`;
}

function ExclusionsTab({ userId }) {
  const [exclusions, setExclusions] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);

  const [addScope,   setAddScope]   = useState("");
  const [addFrom,    setAddFrom]    = useState("");
  const [addTo,      setAddTo]      = useState("");
  const [addBusy,    setAddBusy]    = useState(false);
  const [addError,   setAddError]   = useState(null);

  const [editingId,  setEditingId]  = useState(null);
  const [editFrom,   setEditFrom]   = useState("");
  const [editTo,     setEditTo]     = useState("");
  const [editBusy,   setEditBusy]   = useState(false);
  const [editError,  setEditError]  = useState(null);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await userExclusionsApi.list(userId);
      setExclusions(data || []);
    } catch (e) {
      setError(e.message || "Failed to load exclusions");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    setAddError(null);
    if (!addScope) { setAddError("Select a scope."); return; }
    if (!addFrom)  { setAddError("From date is required."); return; }
    setAddBusy(true);
    try {
      await userExclusionsApi.create(userId, {
        scope: addScope,
        from_date: addFrom,
        to_date: addTo || null,
      });
      setAddScope(""); setAddFrom(""); setAddTo("");
      await load();
    } catch (e) {
      setAddError(e.message || "Failed to add exclusion");
    } finally {
      setAddBusy(false);
    }
  };

  const startEdit = (ex) => {
    setEditingId(ex.id);
    setEditFrom(ex.from_date ? String(ex.from_date).split("T")[0] : "");
    setEditTo(ex.to_date   ? String(ex.to_date).split("T")[0]   : "");
    setEditError(null);
  };

  const cancelEdit = () => { setEditingId(null); setEditError(null); };

  const handleSaveEdit = async (ex) => {
    setEditError(null);
    if (!editFrom) { setEditError("From date is required."); return; }
    setEditBusy(true);
    try {
      await userExclusionsApi.update(userId, ex.id, {
        from_date: editFrom,
        to_date: editTo || null,
      });
      setEditingId(null);
      await load();
    } catch (e) {
      setEditError(e.message || "Failed to update exclusion");
    } finally {
      setEditBusy(false);
    }
  };

  const handleDelete = async (ex) => {
    if (!window.confirm(`Remove "${SCOPE_LABELS[ex.scope] || ex.scope}" exclusion?`)) return;
    try {
      await userExclusionsApi.delete(userId, ex.id);
      setExclusions(prev => prev.filter(e => e.id !== ex.id));
    } catch (e) {
      alert(e.message || "Failed to delete exclusion");
    }
  };

  return (
    <div className="useredit-excl">
      {loading && <p className="useredit-excl-state">Loading…</p>}
      {error   && <p className="useredit-excl-state useredit-excl-state--err">{error}</p>}

      {!loading && (
        <table className="useredit-excl-table">
          <thead>
            <tr>
              <th>Scope</th>
              <th>From</th>
              <th>To</th>
              <th>Added by</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {exclusions.length === 0 && (
              <tr>
                <td colSpan={5} className="useredit-excl-empty">No exclusions yet</td>
              </tr>
            )}
            {exclusions.map(ex => (
              <tr key={ex.id}>
                <td className="useredit-excl-scope">{SCOPE_LABELS[ex.scope] || ex.scope}</td>
                {editingId === ex.id ? (
                  <>
                    <td>
                      <input
                        type="date"
                        className="v2drw-control useredit-excl-date"
                        value={editFrom}
                        max={editTo || undefined}
                        onChange={e => setEditFrom(e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="date"
                        className="v2drw-control useredit-excl-date"
                        value={editTo}
                        min={editFrom || undefined}
                        onChange={e => setEditTo(e.target.value)}
                        placeholder="Open-ended"
                      />
                    </td>
                    <td>{ex.created_by_name || "—"}</td>
                    <td className="useredit-excl-actions">
                      <button
                        type="button"
                        className="useredit-excl-btn useredit-excl-btn--save"
                        disabled={editBusy}
                        onClick={() => handleSaveEdit(ex)}
                      >{editBusy ? "…" : "Save"}</button>
                      <button
                        type="button"
                        className="useredit-excl-btn useredit-excl-btn--cancel"
                        disabled={editBusy}
                        onClick={cancelEdit}
                      >Cancel</button>
                    </td>
                  </>
                ) : (
                  <>
                    <td>{fmtDate(ex.from_date)}</td>
                    <td>{ex.to_date ? fmtDate(ex.to_date) : <span className="useredit-excl-open">Open-ended</span>}</td>
                    <td>{ex.created_by_name || "—"}</td>
                    <td className="useredit-excl-actions">
                      <button
                        type="button"
                        className="useredit-excl-btn useredit-excl-btn--edit"
                        onClick={() => startEdit(ex)}
                        title="Edit dates"
                      >Edit</button>
                      <button
                        type="button"
                        className="useredit-excl-btn useredit-excl-btn--del"
                        onClick={() => handleDelete(ex)}
                        title="Remove"
                      >✕</button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editError && <p className="useredit-excl-state useredit-excl-state--err">{editError}</p>}

      <div className="useredit-excl-add">
        <p className="useredit-excl-add-label">Add exclusion</p>
        <div className="useredit-excl-add-row">
          <select
            className="v2drw-control useredit-excl-scope-sel"
            value={addScope}
            onChange={e => setAddScope(e.target.value)}
          >
            <option value="">— Scope —</option>
            {SCOPE_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <input
            type="date"
            className="v2drw-control useredit-excl-date"
            value={addFrom}
            max={addTo || undefined}
            onChange={e => setAddFrom(e.target.value)}
            placeholder="From"
          />
          <input
            type="date"
            className="v2drw-control useredit-excl-date"
            value={addTo}
            min={addFrom || undefined}
            onChange={e => setAddTo(e.target.value)}
            placeholder="To (optional)"
          />
          <button
            type="button"
            className="useredit-excl-btn useredit-excl-btn--add"
            disabled={addBusy}
            onClick={handleAdd}
          >{addBusy ? "…" : "+ Add"}</button>
        </div>
        {addError && <p className="useredit-excl-state useredit-excl-state--err">{addError}</p>}
      </div>
    </div>
  );
}

function lastSignInText(saved) {
  if (!saved?.last_login_method) return "Never (not recorded yet)";
  const isEmail = saved.last_login_method === "email";
  const when = isEmail ? saved.last_email_login_at : saved.last_msal_login_at;
  return `${isEmail ? "Email" : "Microsoft"} · ${fmtDate(when)}`;
}

const MICROSOFT_STATUS_TONE = { "invitation sent": "info", accepted: "ok", "logged in": "ok" };

function signInStatus(method, saved, overrides) {
  if (method === "microsoft") {
    const st = String(saved?.invitation_status || "").trim();
    if (!st) return { label: "Not invited", tone: "muted" };
    return { label: st, tone: MICROSOFT_STATUS_TONE[st.toLowerCase()] || "muted" };
  }
  const invitedAt = overrides.invitedAt || saved?.email_login_invited_at;
  if (overrides.totpReset) return { label: "2FA reset · awaiting setup", tone: "warn" };
  if (saved?.totp_confirmed_at) return { label: `Registered${saved.email_login_registered_at ? ` · ${fmtDate(saved.email_login_registered_at)}` : ""}`, tone: "ok" };
  if (saved?.email_login_registered_at) return { label: "2FA reset · awaiting setup", tone: "warn" };
  if (invitedAt) return { label: `Invited · ${fmtDate(invitedAt)}`, tone: "info" };
  return { label: "Not invited", tone: "muted" };
}

// External users have exactly ONE sign-in method, stored as
// email_login_enabled (true = Email, false = Microsoft). The invitation and
// status shown follow the SAVED method, so actions wait until a method
// change has been saved. Switching method ends the user's live sessions
// (server-side), and the old method stops working.
function SignInSection({ draft, savedUser, onDraftChange, onMicrosoftInvite, microsoftInviteBusy }) {
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState(null);
  const [overrides, setOverrides] = useState({});

  useEffect(() => { setMsg(null); setOverrides({}); }, [draft.id]);

  const savedMethod = savedUser?.email_login_enabled ? "email" : "microsoft";
  const draftMethod = draft.email_login_enabled ? "email" : "microsoft";
  const dirty = draftMethod !== savedMethod;
  const status = signInStatus(savedMethod, savedUser, overrides);
  const emailRegistered = !!savedUser?.email_login_registered_at || !!savedUser?.totp_confirmed_at;
  const microsoftAccepted = /accepted|logged in/i.test(String(savedUser?.invitation_status || ""));

  const run = async (kind, fn, onOk) => {
    setBusy(kind); setMsg(null);
    try {
      const res = await fn();
      onOk?.();
      setMsg({ tone: "ok", text: res?.message || "Done" });
    } catch (e) {
      setMsg({ tone: "err", text: e.message });
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="userc-field useredit-span-2 useredit-emaillogin">
      <label className="v2drw-form-label">Sign-in method</label>
      <div className="useredit-emaillogin__row">
        <select
          className="v2drw-control"
          value={draftMethod}
          onChange={(e) => onDraftChange("email_login_enabled", e.target.value === "email")}
        >
          <option value="microsoft">Microsoft (guest account)</option>
          <option value="email">Email (password + authenticator)</option>
        </select>
        {!dirty && (
          <span className={`useredit-emaillogin__status useredit-emaillogin__status--${status.tone}`}>{status.label}</span>
        )}
      </div>

      {dirty ? (
        <span className="useredit-emaillogin__hint">
          {draftMethod === "email"
            ? "Saving switches this user to Email sign-in: Microsoft sign-in stops working and they are signed out. Send the email setup invite after saving."
            : "Saving switches this user to Microsoft sign-in: email sign-in stops working and they are signed out. Send the Microsoft invitation after saving."}
        </span>
      ) : (
        <div className="useredit-emaillogin__actions">
          {savedMethod === "microsoft" ? (
            <button
              type="button"
              className="v2drw-btn-cancel"
              disabled={!!busy || microsoftInviteBusy}
              onClick={() => onMicrosoftInvite?.()}
              title="Invite as a Microsoft (B2B) guest and add to a customer group"
            >
              {microsoftAccepted ? "Re-send Microsoft invitation" : "Send Microsoft invitation"}
            </button>
          ) : (
            <>
              <button
                type="button"
                className="v2drw-btn-cancel"
                disabled={!!busy}
                onClick={() => run("invite", () => emailAuthApi.sendInvite(draft.id),
                  () => setOverrides((o) => ({ ...o, invitedAt: new Date().toISOString(), totpReset: false })))}
                title="Email a one-time setup link (valid 72h). Re-sending replaces any earlier link."
              >
                {busy === "invite" ? "Sending…" : emailRegistered ? "Re-send email setup invite" : "Send email setup invite"}
              </button>
              {emailRegistered && (
                <button
                  type="button"
                  className="v2drw-btn-cancel"
                  disabled={!!busy}
                  onClick={() => {
                    if (!window.confirm("Reset two-factor authentication? The user is signed out and must scan a new QR code (emailed link) before signing in again.")) return;
                    run("totp", () => emailAuthApi.resetTotp(draft.id), () => setOverrides((o) => ({ ...o, totpReset: true })));
                  }}
                >
                  {busy === "totp" ? "Resetting…" : "Reset 2FA"}
                </button>
              )}
            </>
          )}
        </div>
      )}
      {msg && <span className={`useredit-emaillogin__msg useredit-emaillogin__msg--${msg.tone}`}>{msg.text}</span>}
    </div>
  );
}

export default function UserEditDrawer({
  open,
  onClose,
  onSave,
  saving = false,
  draft,
  onDraftChange,
  users = [],
  teams = [],
  customers = [],
  projects = [],
  teamId = "",
  onTeamIdChange,
  customerIds = [],
  onCustomerIdsChange,
  projectIds = [],
  onProjectIdsChange,
  onInvite,
  canInvite = false,
  inviteBusy = false,
  isAdmin = false,
  actorRole = "",
}) {
  const isSuperAdmin = String(actorRole || "").trim().toLowerCase() === "super admin";
  const roleOptions = ROLE_OPTIONS.filter((r) => r !== "Super Admin" || isSuperAdmin);
  const [tab, setTab] = useState("details");
  const draftId = draft?.id;
  const draftUserType = draft?.user_type;
  const draftRole = draft?.role;

  useEffect(() => {
    if (open) setTab("details");
  }, [open]);

  const managerOptions = users
    .filter((u) => u.id !== draftId)
    .map((u) => ({ id: u.id, label: u.display_name || u.user_name || "Unnamed" }));
  const teamOptions = teams.map((t) => ({ id: t.id, label: t.team_name || "Unnamed Team" }));
  const clientTeamOption = useMemo(
    () => teamOptions.find((t) => String(t.label || "").trim().toLowerCase() === "client team") || null,
    [teamOptions]
  );
  const normalizedUserType = String(draftUserType || "").trim().toLowerCase();
  const lockRoleAndTeam = normalizedUserType === "external" || normalizedUserType === "admin";
  const customerOptions = customers.map((c) => ({ id: c.id, label: c.customer_name || "Unnamed Customer" }));
  const projectOptions = projects.map((p) => ({
    id: p.id,
    label: p.customer_name ? `${p.project_name} (${p.customer_name})` : (p.project_name || "Unnamed Project"),
  }));

  useEffect(() => {
    if (!open || !draftId || !lockRoleAndTeam) return;
    if (draftRole !== "External") {
      onDraftChange("role", "External");
    }
    const clientTeamId = clientTeamOption?.id || "";
    if (clientTeamId && teamId !== clientTeamId) {
      onTeamIdChange?.(clientTeamId);
    }
  }, [open, draftId, lockRoleAndTeam, draftRole, teamId, clientTeamOption, onDraftChange, onTeamIdChange]);

  if (!open || !draft) return null;

  const footer = (
    <>
      <span className="v2drw-foot-spacer" />
      <button className="v2drw-btn-cancel" type="button" onClick={onClose} disabled={saving || inviteBusy}>Cancel</button>
      <button className="v2drw-btn-save" type="button" onClick={onSave} disabled={saving || inviteBusy}>
        {saving ? "Saving..." : "Save"}
      </button>
    </>
  );

  return (
    <SideDrawerShell
      open={open}
      onClose={() => {
        if (!saving && !inviteBusy) onClose?.();
      }}
      subtitle="Users"
      title={`Edit User · ${draft.display_name || ""}`}
      footer={footer}
      width="min(680px, 100vw)"
    >
      <DrawerTabs
        tabs={[
          { key: "details", label: "Details", icon: "user" },
          { key: "access", label: "Access", icon: "access" },
          ...(isAdmin ? [{ key: "exclusions", label: "Exclusions", icon: "exclusions" }] : []),
        ]}
        activeTab={tab}
        onChange={setTab}
      />

      {tab === "details" && (
        <div className="userc-grid useredit-grid">
          <div className="userc-field">
            <label className="v2drw-form-label">Name</label>
            <input
              className="v2drw-control"
              value={draft.display_name || ""}
              onChange={(e) => onDraftChange("display_name", e.target.value)}
            />
          </div>

          <div className="userc-field">
            <label className="v2drw-form-label">User ID</label>
            <input
              className="v2drw-control"
              value={draft.user_id || ""}
              maxLength={10}
              onChange={(e) => onDraftChange("user_id", (e.target.value || "").replace(/[^a-z0-9]/gi, "").slice(0, 10))}
            />
          </div>

          <div className="userc-field useredit-span-2">
            <label className="v2drw-form-label">Email / Username</label>
            <input className="v2drw-control" value={draft.user_name || ""} readOnly />
          </div>

          <div className="userc-field">
            <label className="v2drw-form-label">User Type</label>
            <select className="v2drw-control" value={draft.user_type || ""} onChange={(e) => onDraftChange("user_type", e.target.value)}>
              {USER_TYPE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt || "—"}</option>
              ))}
            </select>
          </div>

          <div className="userc-field">
            <label className="v2drw-form-label">Role</label>
            <select
              className="v2drw-control"
              value={draft.role || ""}
              disabled={lockRoleAndTeam}
              onChange={(e) => onDraftChange("role", e.target.value)}
            >
              {roleOptions.map((opt) => (
                <option key={opt} value={opt}>{opt || "—"}</option>
              ))}
            </select>
          </div>

          <div className="userc-field">
            <label className="v2drw-form-label">Reporting Manager</label>
            <SearchableSelect
              options={[
                { value: "", label: "— None —" },
                ...managerOptions.map((opt) => ({ value: opt.id, label: opt.label })),
              ]}
              value={draft.reporting_manager_id || ""}
              onChange={(v) => {
                const next = v || null;
                onDraftChange("reporting_manager_id", next);
                onDraftChange("reporting_manager_name", users.find((x) => x.id === next)?.display_name || null);
              }}
              placeholder="Select reporting manager"
            />
          </div>

          <div className="userc-field">
            <label className="v2drw-form-label">Team</label>
            <select
              className="v2drw-control"
              value={teamId || ""}
              disabled={lockRoleAndTeam}
              onChange={(e) => onTeamIdChange?.(e.target.value || "")}
            >
              <option value="">— None —</option>
              {teamOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="userc-field">
            <label className="v2drw-form-label">Mobile</label>
            <input
              className="v2drw-control"
              value={draft.mobile_no || ""}
              onChange={(e) => onDraftChange("mobile_no", e.target.value)}
            />
          </div>

          <div className="userc-field">
            <label className="v2drw-form-label">Status</label>
            <select
              className="v2drw-control"
              value={draft.is_active ? "true" : "false"}
              onChange={(e) => onDraftChange("is_active", e.target.value === "true")}
            >
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          <div className="userc-field">
            <label className="v2drw-form-label">Last sign-in</label>
            <input className="v2drw-control" value={lastSignInText(users.find((u) => u.id === draft.id))} readOnly />
          </div>

          {normalizedUserType === "external" && (
            <SignInSection
              draft={draft}
              savedUser={users.find((u) => u.id === draft.id)}
              onDraftChange={onDraftChange}
              onMicrosoftInvite={canInvite ? onInvite : undefined}
              microsoftInviteBusy={inviteBusy}
            />
          )}
        </div>
      )}

      {tab === "access" && (
        <div className="userc-grid useredit-grid">
          {normalizedUserType === "external" ? (
            <div className="userc-field">
              <label className="v2drw-form-label">Customer</label>
              <select
                className="v2drw-control"
                value={customerIds[0] || ""}
                onChange={(e) => onCustomerIdsChange(e.target.value ? [e.target.value] : [])}
              >
                <option value="">— None —</option>
                {customerOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.label}</option>
                ))}
              </select>
            </div>
          ) : (
            <MultiSelectDropdown
              label="Customers"
              options={customerOptions}
              selectedIds={customerIds}
              onChange={onCustomerIdsChange}
              allLabel="All Customers"
              placeholder="Select customers..."
            />
          )}

          <div className="useredit-span-2">
            <MultiSelectDropdown
              label="Projects"
              options={projectOptions}
              selectedIds={projectIds}
              onChange={onProjectIdsChange}
              allLabel="All Projects"
              placeholder="Select projects..."
            />
          </div>
        </div>
      )}

      {tab === "exclusions" && isAdmin && (
        <ExclusionsTab userId={draft.id} />
      )}
    </SideDrawerShell>
  );
}
